import type { RoleHelper } from '@/helpers/RoleHelper.ts'
import { useGeneratedCmsPageStore, type WhoAmI } from '@/stores'
import type { Router, RouteRecordRaw } from 'vue-router'
import { computed, type ComputedRef, ref, type Ref } from 'vue'
import type { CmsPageJsonldDefaultRead } from '@/api'
import CmsView from '@/views/cms/CmsView.vue'
import { SecuredRoute } from '@/helpers/router.ts'

// Definiere Konstanten für wiederkehrende Strings
const DEFAULT_NAVIGATION = '__site_navigation__'
const PUBLIC_ACCESS = 'PUBLIC_ACCESS'

type CmsRoute = SecuredRoute & {}
class CmsService {
  // Verwende private Felder mit Ref, um die Routen zu speichern
  readonly #cmsRoutes: Ref<Map<string, CmsRoute[]>> = ref<Map<string, CmsRoute[]>>(new Map())

  /**
   * Setzt die CMS-Routen im Router und fügt sie dynamisch hinzu
   * @param router - Der Vue-Router
   */
  async setup(router: Router) {
    try {
      // Hole alle CMS-Seiten aus dem Store
      const cmsInjections = await useGeneratedCmsPageStore()
        .findAll({
          query: {
            'order[sort]': 'asc',
            'properties[]': ['id', 'slug', 'firewall', 'title', 'extends', 'layout'],
            hidden: false
          }
        })
        .then((d) => d.value)
      // Iteriere über die CMS-Seiten und konvertiere sie in Routen
      for (const cmsNav of cmsInjections) {
        const parentKey = cmsNav.extends ?? DEFAULT_NAVIGATION
        if (!this.#cmsRoutes.value.has(parentKey)) {
          this.#cmsRoutes.value.set(parentKey, [])
        }
        const parent = this.#cmsRoutes.value.get(parentKey)
        if (parent) parent.push(this.#cmsConvertToRoute(cmsNav))
      }
      // Füge die Routen dem Router hinzu
      for (const [rtKey, routes] of this.#cmsRoutes.value.entries()) {
        for (const rt of routes) {
          // if (rtKey === DEFAULT_NAVIGATION)
          rt.path = `/cms/${rt.path}`
          router.addRoute(rtKey, rt as RouteRecordRaw)
        }
      }
    } catch (error) {
      console.error('Error setting up CMS routes:', error)
      // Hier kannst du eine benutzerdefinierte Fehlerbehandlung hinzufügen
    }
  }

  /**
   * Gibt alle CMS-Routen zurück
   * @returns - ComputedRef mit allen CMS-Routen
   */
  getRoutes() {
    return computed(() => this.#cmsRoutes.value)
  }

  /**
   * Gibt die Routen für einen bestimmten Elternnamen zurück
   * @param parentName - Der Name des Elternteils
   * @returns - ComputedRef mit den Routen für den Elternnamen
   */
  getRoutesForName(parentName?: string): ComputedRef<CmsRoute[]> {
    //cms_018ee12c-3729-7b68-acfa-3f7d03446872
    if (!parentName) return computed(() => [])
    if (parentName.startsWith('cms_')) parentName = parentName.slice(4)
    return computed(() => this.#cmsRoutes.value.get(parentName) ?? [])
  }

  /**
   * Konvertiert eine CMS-Seite in ein CmsRoute-Objekt
   * @param n - Die CMS-Seite
   * @returns - Das CmsRoute-Objekt
   */
  #cmsConvertToRoute(n: CmsPageJsonldDefaultRead): CmsRoute {
    // Definiere die Sicherheitslogik basierend auf der Firewall
    let security: ((roleHelper: RoleHelper, whoami: WhoAmI) => Awaited<boolean>) | undefined = undefined

    if (n.firewall?.length && n.firewall[0] !== PUBLIC_ACCESS) {
      const rtFw = n.firewall
      security = (rh) => {
        return rtFw.every((role: string) => rh.has(role))
      }
    }
    // Erstelle das CmsRoute-Objekt
    return {
      name: `cms_${n.id}`,
      props: true,
      component: markRaw(CmsView),
      meta: {
        cms: {
          pageid: n.id as string
        },
        title: n.title,
        security: security
      },
      path: (n.slug ?? n.id) as string,
      children: []
    }
  }
}

// Erstelle eine Instanz des CmsService und exportiere sie
const _s = new CmsService()
export default _s
